/**
 * <AccountPickerPage> — « Qui utilise Masteur ? », le choix du compte (page).
 *
 * L'arrivée d'une connexion quand le compte est un élève ou que plusieurs comptes sont gardés sur
 * l'appareil, et celle de « Changer de compte » depuis l'app. La page pose la question en tête
 * (les mots autour du logo), un groupe par famille séparé d'un filet, puis « Ajouter un compte », et
 * plus bas, en lien discret, « Gérer les comptes », qui ouvre le mode de retrait (`managing`).
 *
 * Contrôlée de bout en bout : les groupes arrivent triés, ce qui s'ouvre (`opening`) et le retrait
 * à confirmer (`removal`) arrivent en props, chaque geste remonte en callback. Le retrait est en
 * prop et non dans la page parce qu'il est la première étape d'un geste métier : le view-model
 * doit connaître sa cible pour révoquer les comptes, puis refermer la boîte.
 *
 * La coquille est `<AuthTemplate column="wide">` : la même tête et le même fond que les autres
 * écrans d'avant connexion, mais une colonne de 600 px sans carte — une grille, pas un formulaire.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Divider } from "../../atoms/Divider"
import { Logo } from "../../atoms/Logo"
import { Button } from "../../atoms/Buttons/Button"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Text } from "../../atoms/typography/Text"
import { Title } from "../../atoms/typography/Title"
import { AccountPickerTile } from "../../molecules/AccountPickerTile"
import { ConfirmDialog } from "../../molecules/ConfirmDialog"
import { ErrorText } from "../../atoms/typography/ErrorText"
import { EmptyState } from "../../molecules/EmptyState"
import { AccountPickerGroup } from "../../organisms/AccountPickerGroup"
import { AUTH_LOGO_HEIGHT, AuthTemplate } from "../../templates/AuthTemplate"
import {
    type UIAccountPickerRemoval,
    type UIAccountPickerTarget,
    type UIStoredAccount,
    type UIStoredAccountFamily,
    type UIStoredAccountGroup,
} from "../../../ports/primary/ui/types"

export type AccountPickerPageProps = {
    /** Les groupes, dans l'ordre d'affichage : les familles, puis les comptes hors famille. */
    groups: UIStoredAccountGroup[]
    /** Ce qui s'ouvre : sa tuile tourne, toutes les autres ne répondent plus. */
    opening?: UIAccountPickerTarget | null
    onOpenAccount?: (account: UIStoredAccount) => void
    onOpenParentSpace?: (group: UIStoredAccountGroup) => void
    onAddAccount?: () => void
    /**
     * Le mode de retrait, ouvert par « Gérer les comptes » : la tête devient « Retirer un compte »,
     * un compte hors famille porte la marque du retrait, une famille son « Retirer la famille »
     * (`<AccountPickerGroup>`), « Ajouter un compte » se retire, et « Terminé » ramène à la liste. Le
     * lien n'apparaît qu'avec `onToggleManaging`.
     */
    managing?: boolean
    onToggleManaging?: () => void
    /** L'appui d'un compte hors famille en mode de retrait, et « Retirer la famille » : ils DEMANDENT le retrait, la confirmation suit (`removal`). */
    onRemoveAccount?: (account: UIStoredAccount) => void
    onRemoveFamily?: (family: UIStoredAccountFamily) => void
    /** Le retrait à confirmer. Absent → pas de boîte. */
    removal?: UIAccountPickerRemoval | null
    onConfirmRemoval?: (removal: UIAccountPickerRemoval) => void
    onCancelRemoval?: () => void
    /**
     * Une ouverture a échoué : UNE ligne sous les tuiles, en rouge, annoncée. Absent → rien. Elle
     * reste jusqu'au geste suivant, qui la retire.
     */
    error?: string | null
    testID?: string
}

const NBSP = "\u00A0"

const TEXTS = {
    manage: "G\u00E9rer les comptes",
    manageTitle: "Retirer un compte",
    // Ni « le compte » ni « la famille » : selon l'appareil, on touche l'un, l'autre, ou les deux.
    manageHint: "Touche ce que tu veux retirer de cet appareil.",
    done: "Termin\u00E9",
}

/**
 * Les mots de la confirmation : « Retirer de cet appareil » (décidé le 13/10), parce que le geste ne
 * supprime rien — il oublie le compte sur cet appareil. L'espace devant « ? » est insécable.
 */
/** Ce que retirer un seul compte laisse : le compte, qu'on retrouve en se reconnectant. */
const ONE_ACCOUNT_KEPT = "Son compte n'est pas supprimé : il faudra se reconnecter pour le retrouver ici."

function removalCopy(removal: UIAccountPickerRemoval): { title: string; message: string } {
    if (removal.kind === "account") {
        return { title: `Retirer ${removal.account.firstname} de cet appareil${NBSP}?`, message: ONE_ACCOUNT_KEPT }
    }
    const count = removal.family.students.length + removal.family.others.length
    return {
        title: `Retirer « ${removal.family.name} » de cet appareil${NBSP}?`,
        message:
            count > 1
                ? `Ses ${count} comptes ne sont pas supprimés : il faudra se reconnecter pour les retrouver ici.`
                : ONE_ACCOUNT_KEPT,
    }
}

export function AccountPickerPage(props: AccountPickerPageProps) {
    const {
        groups,
        opening = null,
        managing = false,
        onToggleManaging,
        onOpenAccount,
        onOpenParentSpace,
        onAddAccount,
        onRemoveAccount,
        onRemoveFamily,
        removal = null,
        onConfirmRemoval,
        onCancelRemoval,
        error = null,
        testID,
    } = props

    // La tête dit le mode : on voit qu'on est entré dans le retrait (Wajih, 21/10).
    const header = managing ? (
        <View accessible accessibilityRole="header" accessibilityLabel={TEXTS.manageTitle}>
            <Title size="text-xl" center>
                {TEXTS.manageTitle}
            </Title>
        </View>
    ) : (
        <View accessible accessibilityRole="header" accessibilityLabel="Qui utilise Masteur ?">
            <Row gap="2" justify="center">
                <Title size="text-xl">Qui utilise</Title>
                <Logo height={AUTH_LOGO_HEIGHT} />
                <Title size="text-xl">?</Title>
            </Row>
        </View>
    )

    return (
        <>
            <AuthTemplate header={header} column="wide" testID={testID}>
                <View style={styles.body}>
                    {managing && groups.length > 0 && (
                        <View style={styles.manageHint}>
                            <Text size="text-md" color="text-muted" center>
                                {TEXTS.manageHint}
                            </Text>
                        </View>
                    )}
                    {groups.length === 0 && <EmptyState title="Aucun compte trouvé" />}
                    {groups.map((group, index) => (
                        <React.Fragment key={`${group.kind}-${group.id}`}>
                            {index > 0 && <Divider spacing="8" />}
                            <AccountPickerGroup
                                group={group}
                                alone={groups.length === 1}
                                opening={opening}
                                managing={managing}
                                onOpenAccount={onOpenAccount}
                                onOpenParentSpace={onOpenParentSpace}
                                onRemoveAccount={onRemoveAccount}
                                onRemoveFamily={onRemoveFamily}
                            />
                        </React.Fragment>
                    ))}
                    {error != null && error !== "" && (
                        <View style={styles.error}>
                            <ErrorText center live>
                                {error}
                            </ErrorText>
                        </View>
                    )}
                    {managing ? (
                        // « Terminé » reste tant que le mode dure, même quand le dernier compte vient
                        // d'être retiré : c'est lui qui rend « Ajouter un compte ».
                        <View style={styles.done}>
                            <Button label={TEXTS.done} onPress={onToggleManaging} disabled={opening != null} />
                        </View>
                    ) : (
                        <Stack align="center" style={styles.add}>
                            <AccountPickerTile variant="add" disabled={opening != null} onPress={onAddAccount} />
                            {/* Un lien tertiaire, plus bas et plus discret qu'« Ajouter un compte » : le
                                geste rare ne doit pas se lire comme le geste d'à côté (Wajih, 21/10). */}
                            {onToggleManaging != null && groups.length > 0 && (
                                <View style={styles.manage}>
                                    <TextButton
                                        label={TEXTS.manage}
                                        size="sm"
                                        color="text-muted"
                                        disabled={opening != null}
                                        onPress={onToggleManaging}
                                    />
                                </View>
                            )}
                        </Stack>
                    )}
                </View>
            </AuthTemplate>
            {removal != null && (
                <ConfirmDialog
                    visible
                    {...removalCopy(removal)}
                    confirmLabel="Retirer"
                    onConfirm={() => onConfirmRemoval?.(removal)}
                    onCancel={() => onCancelRemoval?.()}
                />
            )}
        </>
    )
}

const styles = StyleSheet.create((theme) => ({
    // Un peu d'air sous la tête sur grand écran : une grille, pas un formulaire.
    body: {
        paddingTop: { xs: theme.spacing["2"], md: theme.spacing["8"] },
    },
    error: {
        marginTop: theme.spacing["6"],
    },
    add: {
        marginTop: theme.spacing["8"],
    },
    manage: {
        marginTop: theme.spacing["12"],
    },
    manageHint: {
        marginBottom: theme.spacing["6"],
    },
    done: {
        marginTop: theme.spacing["10"],
        alignSelf: "center",
        minWidth: 200,
    },
}))
